/*
 * Sale pricing — shows what a price becomes once the storewide sale is applied.
 *
 * An automatic discount is invisible until checkout: Shopify applies it to the
 * order, not to the product, so a product page shows the full price and the
 * shopper has no idea the sale is real. This writes the discounted figure in
 * front of the original.
 *
 * The rate is read, not discovered. It is already on the page — the sale bar
 * renders it as data-discount-percent — so there is nothing to ask Shopify and
 * this file makes no network request at all.
 *
 * That last part is the whole point. A previous version learned the rate by
 * building a throwaway cart through the Storefront API and reading the total
 * back. Accurate, and ruinous: cartCreate makes a real cart, Shopify cannot
 * tell it from a shopper's, and running it per product page took cart
 * additions from ~20 a day to 127 while cart-to-checkout fell from 19% to
 * 11.8%. Never price by creating a cart.
 *
 * Discount *codes* are handled by prefix, and only by prefix. A code's value is
 * genuinely opaque — the only ways to learn it are to create a cart or to keep
 * a list by hand, and a hand-kept list is what failed here before: five Collabs
 * codes were listed as three. A prefix is not a list. One rule covers every
 * code in a generated batch, including the ones minted after it was written,
 * so it cannot go stale the way an enumeration does.
 *
 * What it buys in robustness it gives up in precision: the rule asserts that
 * every code under the prefix is worth the same, which is true of a batch
 * generated in one go and false the moment somebody mints a cheaper one under
 * the same prefix. That is a merchandising promise, not something this file can
 * check, which is why the rate and the window both live in theme settings.
 *
 * Filename kept for continuity with creator-pricing.css and its .creator-price
 * classes, which the markup below still uses.
 */
(function () {
  'use strict';

  // Who the saving is credited to, resolved once the rate is known.
  var CREDIT = '';

  // Rewrites the number inside the theme's own price string, so currency symbol,
  // separators and placement stay exactly as the theme rendered them.
  var MONEY = /(\d[\d.,]*)/;

  function parseMoney(text) {
    var found = text.match(MONEY);
    if (!found) return null;
    var raw = found[1];
    var normalised = raw.indexOf('.') !== -1 ? raw.replace(/,/g, '') : raw.replace(/,/g, '');
    var value = parseFloat(normalised);
    return isFinite(value) ? { value: value, raw: raw } : null;
  }

  function formatLike(original, value) {
    var money = parseMoney(original);
    if (!money) return null;
    var decimals = money.raw.indexOf('.') === -1 ? 0 : money.raw.split('.')[1].length;
    var formatted = value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return original.replace(money.raw, formatted);
  }

  // The cart is the one place Shopify has already done this arithmetic: it
  // renders final_line_price with the discount taken off. Annotating there would
  // discount an already-discounted figure and show a price below reality. Today
  // those containers hold no .price-item so they're skipped by accident — this
  // makes it deliberate, and keeps it true if the cart markup ever changes.
  var CART_SURFACES = '.cart-item, cart-drawer, #main-cart-items, #main-cart-footer, .cart__footer, .totals, .cart-notification';

  function inCart(element) {
    return typeof element.closest === 'function' && !!element.closest(CART_SURFACES);
  }

  function priceNodeIn(container) {
    var onSale = container.classList.contains('price--on-sale');
    return (
      container.querySelector(onSale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular') ||
      container.querySelector('.price-item--regular')
    );
  }

  // Shopify's own subtotal-vs-total is the source of the percentage; nothing is
  // inferred from the rendered prices. Floored rather than rounded, so a 7.5%
  // discount reads as 7% — understating is honest, overstating is a promise the
  // checkout won't keep.
  function percentOff(rate) {
    var percent = Math.floor(rate * 100 + 0.0001);
    return percent >= 1 && percent <= 99 ? percent : 0;
  }

  // Names the sale if one is running, so the line reads "25% off with Zoey's
  // Birthday Sale" rather than a bare percentage. Nobody is credited: an
  // automatic discount is the store's price for everyone, not something the
  // shopper brought with them.
  function saleCredit() {
    var campaign = window.ZoeyCampaign;
    return campaign && campaign.label ? 'with ' + campaign.label : '';
  }

  // The price the shopper will actually pay goes first; the original drops below
  // it, struck, with the saving beside it.
  function annotate(container, rate) {
    if (container.dataset.creatorPriced || inCart(container)) return;

    var priceNode = priceNodeIn(container);
    if (!priceNode) return;

    var money = parseMoney(priceNode.textContent);
    if (!money || !(money.value > 0)) return;

    var discounted = formatLike(priceNode.textContent.trim(), money.value * (1 - rate));
    if (!discounted) return;

    var amount = document.createElement('span');
    amount.className = 'creator-price__amount';

    var value = document.createElement('span');
    value.textContent = discounted;
    amount.appendChild(value);

    container.insertBefore(amount, container.firstChild);
    container.classList.add('creator-priced');

    // Sits on the second line, next to the struck original: it explains that
    // price, not the one being paid. A creator's code doubles as their handle,
    // so it's credited verbatim — never re-cased, re-spaced or second-guessed.
    var percent = percentOff(rate);
    if (percent) {
      var savings = document.createElement('span');
      savings.className = 'creator-price__savings';
      savings.textContent = CREDIT ? '(' + percent + '% off ' + CREDIT + ')' : '(' + percent + '% off)';
      container.appendChild(savings);
    }
    container.dataset.creatorPriced = 'true';
  }

  // The community page says "X sent you here"; this adds the figure underneath,
  // once Shopify has confirmed it. Only called for a rate that holds across the
  // store — the sentence claims exactly that.
  function announceStorewideRate(rate) {
    var note = document.querySelector('[data-community-discount-note]');
    if (!note) return;

    var percent = percentOff(rate);
    var template = note.getAttribute('data-community-discount-note');
    if (!percent || !template) return;

    note.textContent = template.replace('[percent]', percent).replace('{percent}', percent);
    note.hidden = false;
  }

  // The add-to-bag button carries the price too ("Add to bag · $40.00"). It has
  // to say what the shopper will pay, or it contradicts the price above it.
  //
  // Two things rewrite this span behind us: product-info.js swaps in a fresh one
  // on a variant change, and the Global-e currency script converts the figure
  // in place. Either way the text no longer matches what we wrote, so we treat
  // whatever is there now as the full price and discount it again. Our own
  // write is remembered so it is never discounted twice.
  function annotateButtonPrice(span, rate) {
    var walker = document.createTreeWalker(span, NodeFilter.SHOW_TEXT, null);
    var node;
    while ((node = walker.nextNode())) {
      if (!MONEY.test(node.textContent)) continue;
      if (span.dataset.creatorText && node.textContent === span.dataset.creatorText) return;

      var money = parseMoney(node.textContent);
      if (!money || !(money.value > 0)) return;

      var discounted = formatLike(node.textContent, money.value * (1 - rate));
      if (!discounted) return;

      node.textContent = discounted;
      span.dataset.creatorText = discounted;
      return;
    }
  }

  function annotateAll(rate) {
    var containers = document.querySelectorAll('.price');
    for (var i = 0; i < containers.length; i++) annotate(containers[i], rate);

    var buttons = document.querySelectorAll('.mz-atc-price');
    for (var j = 0; j < buttons.length; j++) annotateButtonPrice(buttons[j], rate);
  }

  // Dawn rewrites the price block when a variant changes or a section re-renders,
  // which drops our line. Watch for that and put it back.
  function keepAnnotated(rate) {
    annotateAll(rate);
    if (typeof MutationObserver !== 'function' || !document.body) return;

    // Dawn rewrites the price block on a variant change or a section re-render,
    // which drops our line. Watch for that and put it back.
    //
    // No re-pricing on variant change any more: a declared rate is a flat
    // percentage, so it is the same on every size. That was only ever needed
    // for a fixed-amount discount, which cannot be declared as one number and
    // is therefore out of scope.
    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;
        annotateAll(rate);
      }, 100);
    }).observe(document.body, { childList: true, subtree: true, characterData: true });
  }

  // Nothing here to write a price onto, and no community note to fill in.
  // Probing anyway would spend Storefront requests discovering a rate with
  // nowhere to put it — this store's collection grid renders no .price at all,
  // which is the difference between three requests per view and none.
  function hasSomewhereToShow() {
    return !!(document.querySelector('.price') || document.querySelector('[data-community-discount-note]'));
  }

  // A storewide automatic discount is a flat, published number — the theme
  // already knows it, because the sale bar renders it. Nothing to ask Shopify.
  function declaredAutomaticRate() {
    var bar = document.querySelector('[data-zoey-bar]');
    if (!bar) return 0;
    var percent = parseInt(bar.getAttribute('data-discount-percent'), 10);
    return percent > 0 && percent < 100 ? percent / 100 : 0;
  }

  // A code is worth nothing to a shopper who is not carrying one, so the rate
  // is gated on the cookie Shopify writes at /discount/CODE — readable, and set
  // only for someone who actually followed the link.
  //
  // The cookie says which code, never whether it still works: Shopify answers
  // 200 and stores an expired code exactly as it stores a live one. So the
  // window is enforced server-side and the marker simply is not rendered once
  // it has passed. Absent marker, no preview, whatever the cookie holds.
  function declaredCodeRate() {
    var marker = document.querySelector('[data-mz-code-sale]');
    if (!marker) return 0;

    var prefix = (marker.getAttribute('data-code-prefix') || '').toUpperCase();
    if (!prefix) return 0;

    var found = ('; ' + document.cookie).match(/; discount_code=([^;]*)/);
    if (!found) return 0;

    var applied = '';
    try {
      applied = decodeURIComponent(found[1]);
    } catch (e) {
      applied = found[1];
    }

    // A creator's code must never pick up the batch rate: theirs is a different
    // number, and only one discount ever lands.
    if (applied.toUpperCase().indexOf(prefix) !== 0) return 0;

    var percent = parseInt(marker.getAttribute('data-code-percent'), 10);
    return percent > 0 && percent < 100 ? percent / 100 : 0;
  }

  function start() {
    if (!hasSomewhereToShow()) return;

    var automatic = declaredAutomaticRate();
    var code = declaredCodeRate();

    // The community note claims a rate that holds across the store. An
    // automatic sale is exactly that; a code's rate holds only for the shopper
    // carrying it, so it never earns the sentence.
    if (automatic > 0) announceStorewideRate(automatic);

    // These codes combine with nothing, so one discount lands and it is the
    // larger — which is what checkout will charge.
    var rate = code > automatic ? code : automatic;
    if (!(rate > 0)) return;

    CREDIT = saleCredit();
    keepAnnotated(rate);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
